<script lang="ts">
    import { PhoneValidationKind } from '$lib/types/PhoneValidationKind';

    import Color from 'color';
    import { onMount, onDestroy } from 'svelte';

    import BirthdayInput from './InputTypes/BirthdayInput.svelte';
    import CheckboxInput from './InputTypes/CheckboxInput.svelte';
    import CityInput from './InputTypes/CityInput.svelte';
    import CountryInput from './InputTypes/CountryInput.svelte';
    import DatePickerInput from './InputTypes/DatePickerInput/DatePickerInput.svelte';
    import DropdownInput from './InputTypes/DropdownInput/DropdownInput.svelte';
    import FileInput from './InputTypes/FileInput.svelte';
    import PhoneInput from './InputTypes/PhoneInput.svelte';
    import PostalInput from './InputTypes/PostalInput.svelte';
    import StreetInput from './InputTypes/StreetInput.svelte';
    import TextareaInput from './InputTypes/TextareaInput.svelte';
    import TextInput from './InputTypes/TextInput.svelte';
    import { getGradientColors, isGradientColor } from '../_utils/color';

    import type { CustomOptions } from '$lib/types/Components/FormQuestion/CustomOptions';
    import type { Language } from '$lib/types/Language';
    import type { Theme } from '$lib/types/Theme';
    import type { JSONContent } from '@tiptap/core';
    import type { FormApi } from 'final-form';
    import type { ComponentType } from 'svelte';

    // Final Form
    export let finalFormInput = {};
    export let form: FormApi<any> | undefined = undefined;

    // Text
    export let theme: Theme;
    export let name = '';
    export let type = '';
    export let placeholder = '';
    export let emoji = '';
    export let showEmoji = true;
    export let conversionTrigger = false;
    export let customOptions: Partial<CustomOptions> = {};
    export let required = false;
    export let pristine = false;
    export let touched = false;
    export let active = false;

    // File
    export let onFileAccept: (file: File) => void = () => null;
    export let uploading = false;

    // Checkbox
    export let wysiwyg: JSONContent | undefined = undefined;

    // Validation
    export let invalid = false;
    export let errorMessage = '';

    // Styling
    export let btnBgColor = '';

    // Localisation
    export let language: Language = 'en';

    let focusColor = btnBgColor;

    // We use the first gradient color as the focus color
    if (isGradientColor(btnBgColor)) {
        const colors = getGradientColors(btnBgColor);

        focusColor = colors[0];
    }

    const outlineColor = Color(focusColor).alpha(0.15).hexa();

    let Input: ComponentType;
    let addressFieldUnsubscribes: Array<(() => void) | null> = [];

    onMount(async () => {
        switch (type) {
            case 'text':
                if (name === 'country' && customOptions?.isAutocompleteEnabled) {
                    Input = CountryInput as ComponentType;
                } else if (name === 'city' && customOptions?.isAutocompleteEnabled) {
                    Input = CityInput as ComponentType;
                } else if (
                    (name === 'address' || name === 'street' || name === 'fullAddress') &&
                    customOptions?.isAutocompleteEnabled
                ) {
                    Input = StreetInput as ComponentType;

                    if (name === 'fullAddress' && form) {
                        // Register 'address.street', 'address.city', 'address.country' and 'address.postalCode' fields with final-form
                        [
                            'address.street',
                            'address.city',
                            'address.country',
                            'address.postalCode',
                        ].forEach((field) => {
                            addressFieldUnsubscribes.push(
                                form.registerField(
                                    field,
                                    () => {}, // Nothing to do here. Just register the field with final-form
                                    { value: true },
                                    { getValidator: () => undefined }, // No need for validation. Field will be set by value from places API
                                ),
                            );
                        });
                    }
                } else if (
                    (name === 'postal' || name === 'zip') &&
                    customOptions?.isAutocompleteEnabled
                ) {
                    Input = PostalInput as ComponentType;
                } else {
                    Input = TextInput as ComponentType;
                }
                break;
            case 'email':
            case 'url':
            case 'number':
                Input = TextInput as ComponentType;
                break;
            case 'birthday':
                Input = BirthdayInput as ComponentType;
                break;
            case 'dropdown':
                Input = DropdownInput as ComponentType;
                break;
            case 'datePicker':
                Input = DatePickerInput as ComponentType;
                break;
            case 'phone':
                if (
                    type === 'phone' &&
                    customOptions?.phoneValidationKind &&
                    [
                        PhoneValidationKind.CountryFormat,
                        PhoneValidationKind.VerificationCode,
                    ].includes(customOptions.phoneValidationKind as PhoneValidationKind)
                ) {
                    Input = PhoneInput as ComponentType;
                } else {
                    Input = TextInput as ComponentType;
                }
                break;
            case 'textarea':
                Input = TextareaInput as ComponentType;
                break;
            case 'file':
                Input = FileInput as ComponentType;
                break;
            case 'checkbox':
                Input = CheckboxInput as ComponentType;
                break;
        }
    });

    onDestroy(() => {
        addressFieldUnsubscribes.forEach((unsubscribe) => {
            if (unsubscribe) {
                unsubscribe();
            }
        });
    });
</script>

<svelte:component
    this={Input}
    {theme}
    {name}
    {type}
    {placeholder}
    {emoji}
    {showEmoji}
    {invalid}
    {active}
    {pristine}
    {required}
    {focusColor}
    {outlineColor}
    {touched}
    {wysiwyg}
    {conversionTrigger}
    {customOptions}
    {finalFormInput}
    {onFileAccept}
    {uploading}
    {errorMessage}
    {language}
    {form}
/>
